/* The payment options the source workbook offers, as a picker.
 *
 * `Reference list` in ASA_Gateway_Quotation.xlsx holds five, and the desk picks
 * one on every quotation. In this product the same question was a blank text
 * field, which is the substitution FR-39 exists to undo one level down: the same
 * agreement arrived as "50/50", "50 / 50 payment" and "half up front", and
 * nothing could group or compare them.
 *
 * A CLIENT-SIDE narrowing, exactly as incoterms.ts is. The server stores payment
 * terms as bounded short text (quote/domain.go) and accepts anything within that
 * length, so a quote holding wording that predates this list still reads back
 * and still prints. Nothing here rejects stored data; it only shapes what is
 * offered next — which is why `current` is always carried into the options.
 *
 * The VALUES are the workbook's English strings, stored verbatim, because they
 * are what prints on the offer and what a five-year-old quote has to keep
 * saying. The LABELS are translated: the reader picking one is our colleague,
 * and the client reads the printed value, not the picker.
 */

/** The five values, in the workbook's own order. */
export const paymentOptionValues = [
  'IN ADVANCE PAYMENT',
  '50/50 PAYMENT (UPON PICKUP&DELIVERY)',
  'FULL PAYMENT UPON DELIVERY (BEFORE UNLOADING)',
  'CREDIT',
  'OTHER',
] as const

/** The i18n key for one value, e.g. `quotes.paymentOptions.credit`. */
function optionKey(value: string): string {
  const slug = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
  return `quotes.paymentOptions.${slug}`
}

/** The picker's options, with any stored value that is not on the list kept as
 *  its own entry so an older quote's terms survive an edit untouched. */
export function paymentOptions({
  current,
  t,
}: {
  current: string
  t: (key: string, fallback?: { defaultValue: string }) => string
}): { value: string; label: string }[] {
  // Annotated rather than inferred: `paymentOptionValues` is `as const`, so an
  // inferred element type would be the five literals and the unshift below —
  // whose value is whatever the quote already stores — would not fit in it.
  const options: { value: string; label: string }[] = paymentOptionValues.map(
    (value) => ({ value, label: t(optionKey(value), { defaultValue: value }) }),
  )

  const stored = current.trim()
  if (stored !== '' && !paymentOptionValues.some((value) => value === stored)) {
    // First, not appended: it is what this quote actually says, and burying it
    // under five it does not say would read as though the field were unset.
    options.unshift({ value: stored, label: stored })
  }

  return options
}
